// @ts-check
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useLocation } from 'react-router'
import { useTheme, Backdrop } from '@material-ui/core'
import Api from 'rest-fetcher-redux'
import Papa from 'papaparse'

import SurveyBulkImport from '~/legacy/pages/Surveys/Survey/SurveyBulkImport'
import {
  useBulkImport,
  MODALS,
  Modal,
  Loading,
  Typography,
} from '~/legacy/components'
import {
  BULK_IMPORT_CONSTANTS,
  BULK_IMPORT_HELPERS,
} from '~/legacy/utils/bulkImportUtils'
import {
  parseGoogleAddressComponents,
  getSearchAddressForDisplay,
  useSurveySelector,
  SnackbarUtils,
} from '~/legacy/utils'
import { GET_DEFAULT_AUTH } from '~/legacy/fetchApi/rootApi'
import { useApiHelper, useResolveSurveyId } from '~/legacy/utils/hooks'

const findCsvValue = (building, ...keys) => {
  const foundKey = keys.reduce((result, key) => {
    if (result != null) return result
    const match = Object.keys(building).find(
      (k) => k.toLowerCase().trim() === key
    )
    if (match && building[match] != null && building[match] !== '')
      return building[match]
    return null
  }, null)
  return foundKey
}

const normalizeCoord = (v) => {
  if (v == null) return null
  const s = typeof v === 'string' ? v.trim() : v
  if (s === '') return null
  const n = Number(s)
  return Number.isNaN(n) ? null : n
}

const isValidLat = (v) => {
  const n = normalizeCoord(v)
  return n != null && n >= -90 && n <= 90
}
const isValidLng = (v) => {
  const n = normalizeCoord(v)
  return n != null && n >= -180 && n <= 180
}
const sanitizeCoord = (v) => {
  const n = normalizeCoord(v)
  return n == null ? null : Math.round(n * 1e6) / 1e6
}

const hasValidCoords = (b) => {
  const lat = findCsvValue(b, 'latitude')
  const lng = findCsvValue(b, 'longitude')
  return lat != null && lng != null && isValidLat(lat) && isValidLng(lng)
}

export default function SurveyImportPage() {
  const { id: urlSurveyId } = useParams()
  const { surveyId, publicId } = useResolveSurveyId(urlSurveyId)
  const { survey } = useSurveySelector(surveyId, null, false)
  const location = useLocation()
  const apiHelper = useApiHelper()

  const [userAcceptedFieldMatches, setUserAcceptedFieldMatches] =
    useState(false)
  const [bulkImportFieldMatchModalOpen, setBulkImportFieldMatchModalOpen] =
    useState(false)
  const [backdropOpen, setBackdropOpen] = useState(false)
  const theme = useTheme()
  const backDropZIndex = theme.zIndex.drawer + 1

  const uploadId = useMemo(() => {
    return new URLSearchParams(location.search).get('upload')
  }, [location.search])

  const uploadType = useMemo(() => {
    return new URLSearchParams(location.search).get('type')
  }, [location.search])

  const uploadFilename = useMemo(() => {
    return new URLSearchParams(location.search).get('filename')
  }, [location.search])

  const {
    setRawCsvHeaders,
    csvHeaderIndexLookup,
    loadedListingsFromCsv,
    setLoadedListingsFromCsv,
    loadedListingsFormatted,
    setLoadedListingsFormatted,
    mappedStandardFields,
    setMappedStandardFields,
    loadedListingFieldsObj,
    setLoadedListingFieldsObj,
    orderedListingFields,
    userMatchableCsvHeaders,
    validatedListings,
    setValidatedListings,
    removedHeaderIndices,
    setRemovedHeaderIndices,
    uploadedCsvFile,
    setUploadedCsvFile,
  } = useBulkImport({ withNewCustomFields: true })

  const handleImport = useCallback(async () => {
    setBackdropOpen(true)

    // When csv we download the file and parse it
    if (uploadType === 'csv') {
      Api.getImportCsv({
        id: uploadId,
      }).then((result) => {
        setUploadedCsvFile(result)

        const url = `${process.env.APP_API_URL}/aws_files/?file_link=${result.uuid}&file_name=${result.name}`
        Papa.parse(url, {
          ...BULK_IMPORT_CONSTANTS.CSV_UPLOAD_PARSER_OPTIONS,
          download: true,
          downloadRequestHeaders: {
            Authorization: GET_DEFAULT_AUTH(),
          },
          complete: (results) => {
            const nonEmptyRowResults = results.data.filter((result) =>
              Object.values(result).find(
                (columnData) => columnData !== undefined && columnData !== null
              )
            )

            BULK_IMPORT_HELPERS.onCsvFileLoaded(
              nonEmptyRowResults,
              setLoadedListingsFromCsv,
              setRawCsvHeaders,
              setBulkImportFieldMatchModalOpen
            )
          },
        })
      })
    }

    if (uploadType === 'pdf') {
      const [fileDownload, extractedData] = await Promise.all([
        Api.getImportPdf({
          id: uploadId,
        }),
        await apiHelper.extractPropertyData({
          filename: uploadFilename,
        }),
      ])

      setUploadedCsvFile(fileDownload)

      // Handle any extraction failures
      if (extractedData.failures && extractedData.failures.length > 0) {
        const message = `Import succeeded, with warnings / partial failures: ${extractedData.failures.join(
          ', '
        )}`
        SnackbarUtils.warning(message)
      }

      BULK_IMPORT_HELPERS.onCsvFileLoaded(
        extractedData.results,
        setLoadedListingsFromCsv,
        setRawCsvHeaders,
        setBulkImportFieldMatchModalOpen
      )
    }
  }, [
    apiHelper,
    setBackdropOpen,
    setUploadedCsvFile,
    setRawCsvHeaders,
    setBulkImportFieldMatchModalOpen,
    setLoadedListingsFromCsv,
    uploadId,
    uploadType,
    uploadFilename,
  ])

  useEffect(() => {
    if (!uploadId || !uploadType) return
    if (!['csv', 'pdf'].includes(uploadType)) {
      throw new Error('Invalid upload type')
    }

    if (uploadType === 'pdf' && !uploadFilename) {
      throw new Error('Upload filename is required for PDF imports')
    }

    handleImport()
  }, [uploadId, uploadType, handleImport, uploadFilename])

  // After loading the listings from the CSV, go through each and try to fetch/populate its address
  useEffect(() => {
    if (loadedListingsFromCsv && csvHeaderIndexLookup) {
      const buildings = loadedListingsFromCsv
      const indexFormattedListingData = []

      // Create an object of the listing/building values. We key off of the index of the header in the csv, rather than
      // the string itself, to handle duplicates / changing names, etc.
      // ie: {0: '18 Tremont Street', ...}
      buildings.forEach((building) => {
        const newCsvFieldArray = {}
        Object.entries(building).forEach(([headerString, value]) => {
          newCsvFieldArray[csvHeaderIndexLookup[headerString]] = value
        })
        indexFormattedListingData.push(newCsvFieldArray)
      })

      const buildCoordsResult = (building, index) => {
        const lat = sanitizeCoord(findCsvValue(building, 'latitude'))
        const lng = sanitizeCoord(findCsvValue(building, 'longitude'))
        const displayAddress = getSearchAddressForDisplay(building)
          || [
            findCsvValue(building, 'address line 1', 'address_1'),
            findCsvValue(building, 'address line 2', 'address_2'),
            findCsvValue(building, 'address line 3', 'address_3'),
            findCsvValue(building, 'address city'),
            findCsvValue(building, 'address postcode', 'postcode', 'postal code'),
          ].filter(Boolean).join(', ')
        const newAddressValues = {
          address: displayAddress,
          city: findCsvValue(building, 'city', 'address city') || '',
          state: findCsvValue(building, 'state', 'province') || '',
          zipcode: findCsvValue(building, 'zipcode', 'zip', 'postal_code', 'postcode', 'address postcode') || '',
          country: findCsvValue(building, 'country') || '',
          latitude: lat,
          longitude: lng,
        }
        return {
          ...indexFormattedListingData[index],
          [BULK_IMPORT_CONSTANTS.FIELDS.ADDRESS.index]: {
            value: {
              GOOGLE_ADDRESS: { ...newAddressValues },
              GOOGLE_AUTOCOMPLETE_RESULTS: {},
            },
          },
        }
      }

      const coordFlags = buildings.map(hasValidCoords)
      const allHaveCoords = coordFlags.every(Boolean)

      if (allHaveCoords) {
        const results = buildings.map((b, i) => buildCoordsResult(b, i))
        setLoadedListingsFormatted(results)
        setBackdropOpen(false)
        return
      }

      const geocodeIndices = []
      const geocodeAddresses = []
      buildings.forEach((building, index) => {
        if (!coordFlags[index]) {
          geocodeIndices.push(index)
          geocodeAddresses.push(getSearchAddressForDisplay(building))
        }
      })

      Api.verifyAddresses({ body: { raw_addresses: geocodeAddresses } }).then((apiResults) => {
        const finalResults = buildings.map((building, index) => {
          if (coordFlags[index]) {
            return buildCoordsResult(building, index)
          }
          return { ...indexFormattedListingData[index] }
        })

        if (apiResults && apiResults.results) {
          const { results } = apiResults
          if (
            results &&
            results.length &&
            results.length === geocodeIndices.length
          ) {
            results.forEach((result, geocodeIdx) => {
              const originalIndex = geocodeIndices[geocodeIdx]
              if (
                result &&
                result.AUTOCOMPLETE_RESULT &&
                result.GEOCODE_RESULT
              ) {
                const addressComponents = parseGoogleAddressComponents(
                  result.GEOCODE_RESULT.address_components
                )
                const newAddressValues = {
                  ...addressComponents,
                  latitude: result.GEOCODE_RESULT.geometry.location.lat,
                  longitude: result.GEOCODE_RESULT.geometry.location.lng,
                }

                finalResults[originalIndex] = {
                  ...indexFormattedListingData[originalIndex],
                  [BULK_IMPORT_CONSTANTS.FIELDS.ADDRESS.index]: {
                    value: {
                      GOOGLE_ADDRESS: { ...newAddressValues },
                      GOOGLE_AUTOCOMPLETE_RESULTS: {
                        ...result.AUTOCOMPLETE_RESULT,
                      },
                    },
                  },
                }
              }
            })
          }
        }
        setLoadedListingsFormatted(finalResults)
        setBackdropOpen(false)
      })
    }
  }, [
    loadedListingsFromCsv,
    csvHeaderIndexLookup,
    setLoadedListingsFormatted,
    setBackdropOpen,
  ])

  // When the user has accepted the field matchings, and we've loaded the listings
  if (
    survey &&
    loadedListingsFormatted &&
    orderedListingFields &&
    userAcceptedFieldMatches
  ) {
    return (
      <SurveyBulkImport
        survey={survey}
        removedHeaderIndices={removedHeaderIndices}
        setRemovedHeaderIndices={setRemovedHeaderIndices}
        validatedListings={validatedListings}
        setValidatedListings={setValidatedListings}
        orderedListingFields={orderedListingFields}
        csvHeaderIndexLookup={csvHeaderIndexLookup}
        loadedListingFieldsObj={loadedListingFieldsObj}
        setLoadedListingFieldsObj={setLoadedListingFieldsObj}
        mappedStandardFields={mappedStandardFields}
        setMappedStandardFields={setMappedStandardFields}
        uploadedCsvFile={uploadedCsvFile}
        onComplete={() => {
          if (location.pathname.includes('databases')) {
            window.location.replace(`/databases/${publicId}`)
          } else {
            window.location.replace(`/surveys/${publicId}`)
          }
        }}
      />
    )
  }

  return (
    <div>
      {/* Loading backdrop for loading the CSV listings after the user selects the csv */}
      <Backdrop
        className="text-white"
        style={{ zIndex: backDropZIndex }}
        open={backdropOpen && !bulkImportFieldMatchModalOpen}
      >
        <div className="flex flex-col items-center">
          <Loading color="inherit" isLoading />
          <Typography className="text-lg">Processing file</Typography>
        </div>
      </Backdrop>

      <Modal
        content={MODALS.BULK_IMPORT_FIELD_MATCH}
        open={bulkImportFieldMatchModalOpen}
        onClose={() => {
          setUserAcceptedFieldMatches(true)
          setBulkImportFieldMatchModalOpen(false)
        }}
        disableBackDropClickClose
        childProps={{
          csvFields: userMatchableCsvHeaders,
          // Fields from the CSV, matched already
          fieldsWithMatches: loadedListingFieldsObj,
          setFieldsWithMatches: (f) => {
            setLoadedListingFieldsObj(f)
          },
          // The fields that we want mappings for
          mappedStandardFields,
          setMappedStandardFields,
          orderedListingFields,
          loadedListingFieldsObj,
          setLoadedListingFieldsObj: (f) => {
            setLoadedListingFieldsObj(f)
          },
        }}
      />
    </div>
  )
}
